import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorState, LoadingState, PageHeader } from '@payment-plan/ui';
import { toPesos } from './money';
import { SeparationFlow, type EstadoInicialFlujo } from './NewSeparationPage';
import type { ApplicantsFormValues, ConditionsFormValues } from './separation.schema';
import { useAuth } from '../../auth/auth-context';
import { apiClient } from '../../lib/api-client';
import { can } from '../../lib/permissions';
import type {
  AvailableUnit,
  ConditionsResult,
  OwnSeparationDetail,
} from '../../types/separations';
import { CierreFeedback, DescartarBorradorButton } from '../consultas/components/CierreSeparacion';

/** Estados en los que el back admite cambios (`ESTADOS_EDITABLES`): borrador y corrección abierta. */
const EDITABLES: ReadonlyArray<OwnSeparationDetail['estado']> = ['BORRADOR', 'CORREGIDA'];

interface Datos {
  detalle: OwnSeparationDetail;
  unidad: AvailableUnit;
  adicionales: AvailableUnit[];
  condiciones: ConditionsResult;
}

/**
 * Retomar una separación en BORRADOR o en CORREGIDA (fix-50 · F-01 de la Línea J): tras «Corregir»
 * (`POST /documents/:id/correccion`) el asesor vuelve al flujo en el paso de condiciones con lo guardado;
 * la generación siguiente emite la versión nueva. Ruta: `/separations/:id/editar`.
 */
export function EditSeparationPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { user } = useAuth();
  const puede = can(user?.permissions, 'separacion.crear');
  // feat-67: al retomar un borrador el asesor puede abandonarlo y liberar la unidad.
  const puedeDescartar = can(user?.permissions, 'separacion.descartar');
  const navigate = useNavigate();
  const [errorDescarte, setErrorDescarte] = React.useState<string | null>(null);

  const datos = useQuery<Datos>({
    queryKey: ['separations', id, 'editar'],
    enabled: puede && Boolean(id),
    queryFn: async () => {
      const detalle = await apiClient.separations.getOwn(id);
      const [unidad, adicionales, condiciones] = await Promise.all([
        apiClient.separations.unit(detalle.unidad.id),
        Promise.all(detalle.unidadesAdicionales.map((u) => apiClient.separations.unit(u.id))),
        apiClient.separations.getConditions(id),
      ]);
      return { detalle, unidad, adicionales, condiciones };
    },
  });

  if (!puede) {
    return <ErrorState title="Acceso restringido" message="No tienes permiso para editar separaciones." />;
  }
  if (datos.isPending) return <LoadingState message="Cargando la separación" />;
  if (datos.isError) {
    return <ErrorState message="No fue posible cargar la separación." onRetry={() => void datos.refetch()} />;
  }

  const { detalle } = datos.data;
  if (!EDITABLES.includes(detalle.estado)) {
    return (
      <ErrorState
        title="La separación no admite cambios"
        message={
          detalle.estado === 'GENERADA'
            ? 'El plan de pagos ya fue generado. Usa «Corregir» para emitir una nueva versión.'
            : 'La separación no admite cambios en su estado actual.'
        }
      />
    );
  }

  const correccion = detalle.estado === 'CORREGIDA';
  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      <PageHeader
        eyebrow="Sala de ventas"
        title={correccion ? 'Corregir separación' : 'Continuar separación'}
        description={
          correccion
            ? 'Ajusta lo necesario y genera la nueva versión del plan de pagos.'
            : 'Retoma la separación donde la dejaste.'
        }
        action={
          !correccion && puedeDescartar ? (
            <DescartarBorradorButton
              separacionId={detalle.id}
              onDescartada={() => void navigate('/separations')}
              onError={setErrorDescarte}
            />
          ) : undefined
        }
      />
      <CierreFeedback exito={null} error={errorDescarte} />
      <SeparationFlow inicial={estadoInicial(datos.data)} />
    </div>
  );
}

function estadoInicial({ detalle, unidad, adicionales, condiciones }: Datos): EstadoInicialFlujo {
  return {
    paso: 3,
    draft: {
      id: detalle.id,
      precioTotal: toPesos(detalle.precioTotal),
      proyecto: detalle.proyecto,
      unidad,
      adicionales,
    },
    separantes: separantesIniciales(detalle),
    condiciones: condicionesIniciales(detalle, unidad, condiciones),
    cuotasGuardadas: detalle.cuotas.map((c) => ({ fechaVencimiento: c.fechaVencimiento, valor: c.valor, tipo: c.tipo })),
  };
}

function separantesIniciales(detalle: OwnSeparationDetail): ApplicantsFormValues | undefined {
  if (detalle.separantes.length === 0) return undefined;
  return {
    autorizacion: detalle.autorizacionDatos,
    separantes: detalle.separantes.map((s) => ({
      tipoPersona: s.tipoPersona,
      nombres: s.nombres ?? '',
      apellidos: s.apellidos ?? '',
      razonSocial: s.razonSocial ?? '',
      tipoDocumento: s.tipoDocumento,
      numeroDocumento: s.numeroDocumento,
      ciudadExpedicion: s.ciudadExpedicion,
      estadoCivil: s.estadoCivil ?? '',
      direccion: s.direccion,
      ciudad: s.ciudad,
      celular: s.celular,
      correo: s.correo,
      porcentaje: String(s.porcentaje),
    })),
  };
}

/**
 * Condiciones guardadas → formulario. fix-64 (H-02 de fix-50): el back guarda el n.º de cuotas, el día de
 * pago y el porcentaje pactado, así que se retoman tal cual (porcentaje → modo PORCENTAJE; si no, VALOR con
 * `cuotaInicialTotal`). Separaciones anteriores (campos `null`): n.º de cuotas deducido de las ORDINARIAS
 * guardadas y día vacío. Sin condiciones guardadas (cuota inicial 0) el paso usa sus valores por defecto.
 */
function condicionesIniciales(
  detalle: OwnSeparationDetail,
  unidad: AvailableUnit,
  c: ConditionsResult,
): ConditionsFormValues | undefined {
  if (toPesos(c.cuotaInicialTotal) <= 0n) return undefined;
  const ordinarias = detalle.cuotas.filter((cuota) => cuota.tipo === 'ORDINARIA').length;
  const numeroCuotas = c.numeroCuotas ?? (ordinarias > 0 ? ordinarias : null);
  const porcentaje = c.cuotaInicialPorcentaje ?? null;
  const porPorcentaje = porcentaje !== null;
  return {
    precioNegociado: toPesos(c.precioNegociadoPrincipal ?? unidad.precioLista).toString(),
    valorSeparacion: toPesos(c.valorSeparacion).toString(),
    modoCuotaInicial: porPorcentaje ? 'PORCENTAJE' : 'VALOR',
    porcentajeCuotaInicial: porcentaje ?? 0,
    valorCuotaInicial: porPorcentaje ? '' : toPesos(c.cuotaInicialTotal).toString(),
    numeroCuotas: numeroCuotas === null ? '' : String(numeroCuotas),
    diaPago: c.diaPago == null ? '' : String(c.diaPago),
    fechaSeparacion: c.fechaSeparacion,
    fechaEscrituracion: c.fechaEscrituracion,
  };
}
